/**
 * PostHog Redux-Saga Implementation
 * Handles all analytics tracking through Redux actions
 */

import { takeEvery, select, call, fork } from 'redux-saga/effects';
import { AnyAction } from 'redux';
import {
  PosthogService,
  PosthogActionTypes,
  PosthogAction,
  REDUX_TO_POSTHOG_EVENT_MAP,
  POSTHOG_EVENTS,
} from '~/services/posthog';
import { getPosthogConfig } from '~/services/posthog/config';
import { hasChildOffices } from '~/utils/OfficeUtils';

/**
 * Helper function to build user properties for identify calls
 * @param user - User object from state or action payload
 * @param childOffices - Child offices array (pass separately since it's stored outside user object in Redux state)
 */
export function buildUserProperties(user: any, childOffices?: unknown[] | null): Record<string, any> {
  // Extract location from address array if available
  const primaryAddress = user.address && user.address[0];

  // Use provided childOffices, or fall back to user.childOffices (for action payloads that still have it)
  const offices = childOffices ?? user.childOffices;

  return {
    // Existing properties
    userType: user.user_type,
    name: user.office_name,
    userId: user.id,
    timeZone: user.time_zone,
    createdAt: user.created_at,
    // Location info from address array (non-PII)
    city: primaryAddress?.city,
    state: primaryAddress?.state,
    zipCode: primaryAddress?.postal_code,

    // Office properties for cohort analysis
    officeType: user.office_info?.officeType ?? null,
    workerClassification: user.workerClassification?.classificationType ?? null,
    isParent: hasChildOffices(offices),
    ramTerritory: user.ram?.first_name ?? null,
    isPreApproved: user.is_pre_approved ?? null,

    // Environment context
    environment: import.meta.env.VITE_TEMP_MEE_ENV || 'development',
  };
}

/**
 * Initialize PostHog on app startup
 */
function* initializePosthogSaga(): Generator<any, void, any> {
  try {
    const config = getPosthogConfig();
    const posthog = PosthogService.getInstance();

    yield call([posthog, 'initialize'], config);

    // If user is already logged in, identify them
    // Note: childOffices is stored separately in state.user.childOffices, not on the user object
    const user: any = yield select((state: any) => state.user.user);
    const childOffices: any = yield select((state: any) => state.user.childOffices);
    if (user && user.id) {
      yield call([posthog, 'identify'], user.id, buildUserProperties(user, childOffices));
    }
  } catch (error) {
    console.error('[PostHog Saga] Initialization error:', error);
  }
}

/**
 * Track Redux actions as PostHog events
 */
function* trackActionSaga(action: AnyAction): Generator<any, void, any> {
  try {
    const posthog = PosthogService.getInstance();

    // Check if this Redux action should be tracked
    const eventName = REDUX_TO_POSTHOG_EVENT_MAP[action.type];

    if (!eventName) {
      // This action doesn't need to be tracked
      return;
    }

    // Get current user state for context
    const user: any = yield select((state: any) => state.user);

    // Build event properties
    const properties: Record<string, any> = {
      // Add user context
      userId: user?.id,
      userRole: user?.role,
      officeId: user?.officeId,

      // Add action context
      actionType: action.type,
      timestamp: new Date().toISOString(),

      // Add environment context
      environment: import.meta.env.VITE_TEMP_MEE_ENV || 'development',
    };

    // Add specific properties based on action type
    switch (action.type) {
      case 'LOGIN_SUCCESS':
        properties.loginMethod = action.payload?.loginMethod || 'email';
        properties.sessionId = action.payload?.sessionId;
        break;

      case 'LOGIN_FAILURE':
        properties.errorMessage = action.payload?.error?.message;
        properties.errorCode = action.payload?.error?.code;
        break;

      case 'LOGOUT_ACTION':
        properties.logoutReason = action.payload?.reason || 'manual';
        break;
    }

    // Track the event
    yield call([posthog, 'track'], eventName, properties);
  } catch (error) {
    console.error('[PostHog Saga] Error tracking action:', action.type, error);
  }
}

/**
 * Handle PostHog-specific actions
 */
function* handlePosthogActionSaga(action: PosthogAction): Generator<any, void, any> {
  const posthog = PosthogService.getInstance();

  try {
    switch (action.type) {
      case PosthogActionTypes.TRACK_EVENT:
        yield call([posthog, 'track'], action.payload.event, action.payload.properties);
        break;

      case PosthogActionTypes.IDENTIFY_USER:
        const { id, ...traits } = action.payload;
        yield call([posthog, 'identify'], id, traits);
        break;

      case PosthogActionTypes.RESET_USER:
        yield call([posthog, 'reset']);
        break;

      case PosthogActionTypes.SET_USER_PROPERTIES:
        yield call([posthog, 'setUserProperties'], action.payload);
        break;

      case PosthogActionTypes.PAGE_VIEW:
        yield call([posthog, 'trackPageView'], action.payload.path, action.payload.properties);
        break;
    }
  } catch (error) {
    console.error('[PostHog Saga] Error handling PostHog action:', action.type, error);
  }
}

/**
 * Watch for user authentication events
 */
function* watchUserAuthentication() {
  const posthog = PosthogService.getInstance();

  // Handle login success
  yield takeEvery('LOGIN_SUCCESS', function* (action: AnyAction): Generator<any, void, any> {
    try {
      const { user } = action.payload;

      if (user && user.id) {
        // Get childOffices from state for proper isParent calculation
        const childOffices: any = yield select((state: any) => state.user.childOffices);

        // Identify the user with PostHog
        yield call([posthog, 'identify'], user.id, buildUserProperties(user, childOffices));

        // Set user properties
        yield call([posthog, 'setUserProperties'], {
          lastLogin: new Date().toISOString(),
          loginCount: (user.loginCount || 0) + 1,
        });
      }
    } catch (error) {
      console.error('[PostHog Saga] Error identifying user:', error);
    }
  });

  // Handle logout
  yield takeEvery(['LOGOUT_ACTION', 'LOGOUT_ACTION_SUCCESS'], function* () {
    try {
      // Track logout event before resetting
      yield call([posthog, 'track'], POSTHOG_EVENTS.OFFICE_AUTH_LOGOUT, {
        timestamp: new Date().toISOString(),
      });

      // Reset PostHog user
      yield call([posthog, 'reset']);
    } catch (error) {
      console.error('[PostHog Saga] Error resetting user:', error);
    }
  });
}

/**
 * Main watcher saga for PostHog
 */
export function* watchPosthogAnalytics() {
  // Initialize PostHog
  yield fork(initializePosthogSaga);

  // Start all watchers
  yield fork(watchUserAuthentication);

  // Watch only for Redux actions that we actually track
  yield takeEvery(Object.keys(REDUX_TO_POSTHOG_EVENT_MAP), trackActionSaga);

  // Watch for PostHog-specific actions
  yield takeEvery(
    [
      PosthogActionTypes.TRACK_EVENT,
      PosthogActionTypes.IDENTIFY_USER,
      PosthogActionTypes.RESET_USER,
      PosthogActionTypes.SET_USER_PROPERTIES,
      PosthogActionTypes.PAGE_VIEW,
    ],
    handlePosthogActionSaga,
  );
}

export default watchPosthogAnalytics;
